import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Txt } from "@/components/Txt";
import { Button, StatusPill } from "@/components/kit";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const FULFILL_TONE = { pending: "gold", fulfilled: "olive", cancelled: "gray" };
const SOURCE = { certificate: "Certificate", card: "Membership Card", membership_card: "Membership Card", reward: "Reward" };
const sourceLabel = (r) => SOURCE[r.kind] || SOURCE[r.source] || "Reward";

export default function Fulfillment({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.fulfillment);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/fulfillment", { params: filter ? { status: filter } : {} });
    setItems(r.data.items); setLoaded(true);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.put(`/fulfillment/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const openMember = (r) => {
    if (!r.member_id) return;
    sessionStorage.setItem("rca_open_member", r.member_id);
    window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: "membership_directory" } }));
  };

  return (
    <div data-testid="fulfillment-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="fulfillment.title" screen="Fulfillment">Fulfillment</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-4"><Txt id="fulfillment.subtitle" screen="Fulfillment">Reward redemptions to hand out. Cancelling refunds the member's points and restocks the item.</Txt></p>
      <div className="flex gap-2 mb-4">
        {["", "pending", "fulfilled", "cancelled"].map((s) => (
          <button key={s || "all"} data-testid={`fulfill-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold border capitalize ${filter === s ? "bg-ink text-cream border-ink" : "border-ink/20"}`}>{s || "all"}</button>
        ))}
      </div>
      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="fulfillment-list">
        {loaded && items.length === 0 && <div className="p-10 text-center text-ink/55">Nothing here.</div>}
        {items.map((r) => (
          <div key={r.redemption_id} data-testid={`fulfill-row-${r.redemption_id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[16px] font-semibold">{r.reward_name}</div>
                <span data-testid={`fulfill-source-${r.redemption_id}`} className="text-[11px] px-2 py-0.5 rounded-full bg-ink/8 font-semibold uppercase tracking-wide text-ink/60">{sourceLabel(r)}</span>
              </div>
              <div className="text-[13px] text-ink/55">
                {r.member_id
                  ? <button data-testid={`fulfill-member-${r.redemption_id}`} onClick={() => openMember(r)} className="font-semibold text-maroon hover:underline">{r.member_name}{r.member_number ? ` · ${r.member_number}` : ""}</button>
                  : <span>{r.member_name}{r.member_number ? ` · ${r.member_number}` : ""}</span>}
                {" · "}{r.point_cost} pts · {fmt(r.created_at)}
              </div>
              {r.note && <div className="text-[13px] text-ink/50 italic mt-0.5">{r.note}</div>}
            </div>
            <div className="flex items-center gap-2">
              {r.member_id && <Button variant="secondary" className="min-h-[40px] py-2 text-[13px]" data-testid={`fulfill-view-member-${r.redemption_id}`} onClick={() => openMember(r)}>View member</Button>}
              <StatusPill tone={FULFILL_TONE[r.status] || "gray"} className="text-[12px] px-2.5 py-1 capitalize" data-testid={`fulfill-status-${r.redemption_id}`}>{r.status}</StatusPill>
              {canEdit && r.status === "pending" && (
                <>
                  <Button variant="olive" className="min-h-[40px] py-2 text-[13px]" data-testid={`fulfill-done-${r.redemption_id}`} onClick={() => setStatus(r.redemption_id, "fulfilled")}>Mark fulfilled</Button>
                  <Button variant="danger" className="min-h-[40px] py-2 text-[13px]" data-testid={`fulfill-cancel-${r.redemption_id}`} onClick={() => setStatus(r.redemption_id, "cancelled")}>Cancel</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
